import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Backdrop, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import SideNavLayout from "../customsidenav/sidenavlayout";
import api from "../api/api";
import { showToast } from "../reduxtoolkit/toastslice";
import { getPracticeId, practiceShortName, roomsAdmin } from "../config/practice";

/*
 * Rooms admin (/rooms), ported from OSA's own build (rooms/Rooms.jsx) for
 * practices whose profile has roomsAdmin (7825). Lists every room, inactive
 * ones too (v1 rooms/fetch), and adds, renames and sets Active/Inactive:
 *  - add: rooms/create_room?roomno=&status= -- the call care's waiting room
 *    makes; it keeps the chosen status and the grid defaults (rooms/create,
 *    which OSA used, always stores Active);
 *  - rename / status: rooms/updt_rm_label, which also moves today's patients
 *    in the old room to the new name (rooms/{id}/update does not).
 * The route needs the profile's Side Bar privilege; delete also needs an
 * admin role (profile deleteRoles), where OSA let anyone delete.
 */

// May this user delete rooms? (user role id, /auth/me grole)
export const canDeleteRooms = (user) => {
  const roles = roomsAdmin()?.deleteRoles || [];
  const grole = user?.grole;
  return grole !== undefined && grole !== null && roles.includes(String(grole).trim());
};

const errorMessage = (e, fallback) => e?.response?.data?.message || fallback;

const Rooms = () => {
  const practiseid = getPracticeId();
  const dispatch = useDispatch();
  const userConfig = useSelector((state) => state.userconfig);
  const canDelete = canDeleteRooms(userConfig);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // room to delete
  const [editingRoom, setEditingRoom] = useState(null);
  const [roomName, setRoomName] = useState("");
  const [roomActive, setRoomActive] = useState("Y");
  const [viewMode, setViewMode] = useState("list"); // 'card' | 'list'

  const toast = (message, severity) => dispatch(showToast({ message, severity }));

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/v1/${practiseid}/rooms/fetch`);
      // 201 + data "" when the practice has no rooms.
      setRooms(res?.status === 200 && Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      toast("Could not load rooms", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = `${practiceShortName()} - Rooms`;
    fetchRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAdd = () => {
    setEditingRoom(null);
    setRoomName("");
    setRoomActive("Y");
    setModalOpen(true);
  };

  const openEdit = (room) => {
    setEditingRoom(room);
    setRoomName(room.room || "");
    setRoomActive(room.is_active === "N" ? "N" : "Y");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRoom(null);
    setRoomName("");
    setRoomActive("Y");
  };

  const handleSave = async () => {
    const name = roomName.trim().toUpperCase();
    if (!name) {
      toast("Room name is required", "error");
      return;
    }
    setLoading(true);
    try {
      const res = editingRoom
        ? await api.put(`/v1/${practiseid}/rooms/updt_rm_label`, {
            old_roomno: String(editingRoom.room || "").toUpperCase(),
            new_roomno: name,
            status: roomActive,
          })
        : await api.post(
            `/v1/${practiseid}/rooms/create_room?roomno=${encodeURIComponent(name)}&status=${roomActive}`,
          );
      if (res?.status === 200) {
        toast(editingRoom ? "Room updated successfully" : "Room created successfully", "success");
        closeModal();
        fetchRooms();
      } else {
        toast(res?.message || "Something went wrong", "error");
      }
    } catch (e) {
      console.error(e);
      toast(errorMessage(e, "Something went wrong"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (room) => {
    if (!canDelete) return;
    setLoading(true);
    try {
      const res = await api.delete(
        `/v1/${practiseid}/rooms/delete-room?room_no=${encodeURIComponent(room.room)}`,
      );
      if (res?.status === 200) {
        toast("Room deleted successfully", "success");
        fetchRooms();
      } else {
        toast(res?.message || "Failed to delete room", "error");
      }
    } catch (e) {
      console.error(e);
      toast(errorMessage(e, "Failed to delete room"), "error");
    } finally {
      setLoading(false);
      setDeleteConfirm(null);
    }
  };

  const statusPill = (room) => (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${
        room.is_active === "Y" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
      }`}
    >
      {room.is_active === "Y" ? "Active" : "Inactive"}
    </span>
  );

  return (
    <SideNavLayout>
      <div className="p-4" data-testid="rooms-page">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-semibold text-[#18181B]">Rooms</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode(viewMode === "list" ? "card" : "list")}
              className="p-2 border border-[#E5E7EB] rounded-lg bg-white text-[#6B7280] hover:bg-[#F3F4F6]"
              title={viewMode === "list" ? "Switch to Card view" : "Switch to List view"}
            >
              {viewMode === "list" ? <ViewModuleIcon fontSize="small" /> : <ViewListIcon fontSize="small" />}
            </button>
            <button
              onClick={openAdd}
              className="flex items-center gap-1 bg-[#1D2951] text-white px-4 py-2 rounded-lg text-sm"
            >
              <AddIcon fontSize="small" /> Room
            </button>
          </div>
        </div>

        {rooms.length === 0 && !loading ? (
          <div className="text-center py-16 text-gray-400">No rooms found</div>
        ) : viewMode === "card" ? (
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
            {rooms.map((room, idx) => (
              <div
                key={room.id || idx}
                className="bg-white border border-[#E5E7EB] rounded-xl p-4 flex flex-col gap-3 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-[#18181B]">{room.room}</span>
                  {statusPill(room)}
                </div>
                <div className="flex gap-2 mt-auto">
                  <button
                    onClick={() => openEdit(room)}
                    className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-[#F3F4F6] hover:bg-[#E5E7EB] text-sm text-[#374151]"
                  >
                    <EditIcon fontSize="small" /> Edit
                  </button>
                  {canDelete && (
                    <button
                      onClick={() => setDeleteConfirm(room)}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-sm text-red-500"
                    >
                      <DeleteIcon fontSize="small" /> Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <tr>
                  <th className=" px-4 py-3 font-semibold text-[#374151]">#</th>
                  <th className=" px-4 py-3 font-semibold text-[#374151]">Room Name</th>
                  <th className=" px-4 py-3 font-semibold text-[#374151]">Status</th>
                  <th className="text-right px-4 py-3 font-semibold text-[#374151]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rooms.map((room, idx) => (
                  <tr key={room.id || idx} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]">
                    <td className="px-4 py-3 text-[#6B7280]">{idx + 1}</td>
                    <td className="px-4 py-3 font-medium text-[#18181B]">{room.room}</td>
                    <td className="px-4 py-3">{statusPill(room)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openEdit(room)}
                        aria-label={`Edit ${room.room}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#F3F4F6] hover:bg-[#E5E7EB] mr-2"
                      >
                        <EditIcon fontSize="small" className="text-[#374151]" />
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => setDeleteConfirm(room)}
                          aria-label={`Delete ${room.room}`}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100"
                        >
                          <DeleteIcon fontSize="small" className="text-red-500" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">{editingRoom ? "Edit Room" : "Add Room"}</h2>
              <CloseIcon className="cursor-pointer text-gray-500" onClick={closeModal} />
            </div>
            <div className="flex flex-col gap-3">
              <div>
                <label htmlFor="rooms-name" className="block text-sm font-medium text-[#374151] mb-1 text-start">
                  Room Name
                </label>
                <input
                  id="rooms-name"
                  type="text"
                  value={roomName}
                  onChange={(e) => setRoomName(e.target.value)}
                  placeholder="Enter room name"
                  className="w-full border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1D2951]"
                />
              </div>
              <div>
                <label htmlFor="rooms-status" className="block text-sm font-medium text-[#374151] mb-1 text-start">
                  Status
                </label>
                <select
                  id="rooms-status"
                  value={roomActive}
                  onChange={(e) => setRoomActive(e.target.value)}
                  className="w-full border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1D2951]"
                >
                  <option value="Y">Active</option>
                  <option value="N">Inactive</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm rounded-lg bg-[#F3F4F6] text-[#374151] hover:bg-[#E5E7EB]"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm rounded-lg bg-[#1D2951] text-white hover:bg-[#16213e]"
              >
                {editingRoom ? "Save Changes" : "Add Room"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal (admins only) */}
      {deleteConfirm && canDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold mb-2">Delete Room</h2>
            <p className="text-sm text-[#6B7280] mb-5">
              Are you sure you want to delete <span className="font-semibold text-[#18181B]">{deleteConfirm.room}</span>?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm rounded-lg bg-[#F3F4F6] text-[#374151] hover:bg-[#E5E7EB]"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </SideNavLayout>
  );
};

export default Rooms;
